<template>
  <div class="relative inline-block">
    <vs-button
      class="bg-white px-4 py-2 lg:py-3 font-semibold text-left focus:outline-none flex justify-between items-center shadow-md w-full sm:w-32 xl:w-full custom-org-switch"
      type="border"
      color="primary"
      icon-pack="feather"
      icon="icon-chevron-down"
      :icon-after="true"
      @click="toggleDropdown"
    >
      <span :class="{'font-semibold': selectedOrg}">{{ selectedOrg ? selectedOrg.name : 'Select Organization' }}</span>
    </vs-button>
    <ul
      v-if="open"
      class="absolute z-10 mt-1 w-full bg-white border border-gray-300 rounded-lg shadow max-h-60 overflow-auto"
    >
      <li
        v-for="org in organizations"
        :key="org._id"
        @click="orgChanged(org._id)"
        class="px-4 py-2 cursor-pointer capitalize flex items-center transition border-l-0 border-r-0 border-t-0 border-b border-gray-400 border-solid text-base sm:text-sm xl:text-base"
        :class="{
          'font-bold': org._id === activeOrganizationId,
          'hover:bg-gray-100': org._id !== activeOrganizationId
        }"
      >
        {{ org.name }}
      </li>
    </ul>
  </div>
</template>

<script>
import { mapActions } from 'vuex';
import _ from 'lodash';
import { reloadOrRedirectFromHome } from '@/utils/routeGuards';

export default {
  name: 'SelectOrganization',
  data() {
    return {
      open: false,
      organizations: [],
      activeOrganizationId: null,
    };
  },
  computed: {
    selectedOrg() {
      return this.organizations.find(org => org._id === this.activeOrganizationId);
    },
  },
  methods: {
    ...mapActions('organization', ['fetchOrganizationClinicsList']),
    toggleDropdown() {
      this.open = !this.open;
    },
    async orgChanged(selectedOrgId) {
      this.open = false;
      this.$vs.loading();

      const selectedOrg = this.organizations.find(org => org._id === selectedOrgId);
      const oldRole = this.getLocalStorageItem('Role');

      if (selectedOrg._id !== this.getLocalStorageItem('OrganizationId')) {
        localStorage.setItem(
          `${process.env.VUE_APP_ACCESS_TOKEN_NAME}Role`,
          selectedOrg.role
        );
        localStorage.setItem(
          `${process.env.VUE_APP_ACCESS_TOKEN_NAME}OrganizationId`,
          selectedOrg._id
        );
        this.activeOrganizationId = selectedOrg._id;
        this.activeOrganizationName = selectedOrg.name;

        try {
          if (selectedOrg.role === 'Org Owner' || selectedOrg.role === 'Staff') {
            const res = await this.fetchOrganizationClinicsList({
              id: selectedOrg._id,
              limit: 200,
              sort: 'clinicName',
              dir: 'asc',
            });
            const clinics = res.data.data.docs
              .filter(({ showStore }) => showStore)
              .map(
                ({
                  _id,
                  clinicName,
                  clinicOpeningHours,
                  address,
                  paymentTypes,
                }) => ({
                  _id,
                  clinicName,
                  clinicOpeningHours,
                  address,
                  paymentTypes,
                })
              );

            if (clinics.length) {
              sessionStorage.setItem(
                'doctorClinicSelectedClinic',
                clinics[0]._id
              );
            } else {
              sessionStorage.clear();
            }

            if (oldRole !== selectedOrg.role) {
              this.$router.push('login');
            } else {
              reloadOrRedirectFromHome(this.$route.path);
            } 
          } else {
            if (oldRole !== selectedOrg.role) {
              if (
                this.$store.state.AppActiveUser.userRole.toLowerCase() ==
                'nurse'
              ) {
                this.$router.push(
                  process.env.VUE_APP_REGION !== 'US'
                    ? '/nurse/notice-board'
                    : '/nurse/patients'
                );
              } else {
                this.$router.push(
                  process.env.VUE_APP_REGION !== 'US'
                    ? '/doctor/notice-board'
                    : '/doctor/treatment-history'
                );
              }
            } else {
              reloadOrRedirectFromHome(this.$route.path);
            }
          }
        } catch (error) {
          console.error(error);
        }

        // Force reload to prevent "RangeError: Maximum call stack size exceeded" when switching to different roles/user types
        reloadOrRedirectFromHome(this.$route.path);
      }
    },
    getOrganizations() {
      const user = JSON.parse(
        localStorage.getItem(`${process.env.VUE_APP_ACCESS_TOKEN_NAME}User`)
      );

      let orgId = localStorage.getItem(
        `${process.env.VUE_APP_ACCESS_TOKEN_NAME}OrganizationId`
      );

      this.organizations = user.organizations;

      // If no orgId in localStorage, default to the first organization
      if (!orgId && this.organizations.length > 0) {
        orgId = this.organizations[0]._id;
        localStorage.setItem(
          `${process.env.VUE_APP_ACCESS_TOKEN_NAME}OrganizationId`,
          orgId
        );
      }

      this.activeOrganizationId = orgId;

      if (this.activeOrganizationId) {
        this.activeOrganizationName =
          user.organizations[
            _.findIndex(user.organizations, ['_id', this.activeOrganizationId])
          ].name;
      }
    },
    getLocalStorageItem(key) {
      return localStorage.getItem(`${process.env.VUE_APP_ACCESS_TOKEN_NAME}${key}`);
    },
    handleClickOutside(event) {
      if (this.open && !this.$el.contains(event.target)) {
        this.open = false;
      }
    },
  },
  watch: {
    activeOrganizationId(newVal, oldVal) {
      if (newVal && newVal !== oldVal) {
        this.orgChanged(newVal);
      }
    },
  },
  async mounted() {
    this.getOrganizations();
    document.addEventListener('click', this.handleClickOutside);
  },
  beforeDestroy() {
    document.removeEventListener('click', this.handleClickOutside);
  },
};
</script>

<style>
.custom-org-switch .vs-button-text {
  white-space: nowrap;
  text-overflow: ellipsis;
  overflow: hidden;
}
</style>